import './qr-reservation.css';

import {
	faCalendar,
	faChevronRight,
	faLock,
} from '@fortawesome/free-solid-svg-icons';
import {
	fetchPropertyInfoByCode,
	fetchUIProperties,
	selectSession,
} from '../../redux/slices/sessionSlice';
import {
	generatePreCheckinLinkToken,
	generateServiceToken,
	getReservationByConfirmationNumber,
	searchReservationByLastNameAndDeparture,
	updateStageStatus,
} from '../../redux/api';
import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/hook';

import { bypassConfirmLeaveOnce } from '../../hooks/confirmLeavePageBypass';
import DatePicker from 'react-datepicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styles from '../../styles/datefieldformat.module.css';
import { t } from 'i18next';
import { useSearchParams } from 'react-router-dom';

export default function QRReservationSearch() {
	const [searchParams] = useSearchParams();
	const { propertyInfo, uiProperties } = useAppSelector(selectSession);
	const dispatch = useAppDispatch();

	const [searchMode, setSearchMode] = useState<'details' | 'confirmation'>(
		'details'
	);

	const [lastName, setLastName] = useState('');
	const [departureDate, setDepartureDate] = useState('');
	const [departureDateObj, setDepartureDateObj] = useState<Date | null>(null);
	const [confirmationNumber, setConfirmationNumber] = useState('');

	const [loading, setLoading] = useState(false);
	const [results, setResults] = useState<any[] | null>(null);
	const [displayedResults, setDisplayedResults] = useState<any[] | null>(null);

	const [showFirstNamePrompt, setShowFirstNamePrompt] = useState(false);
	const [firstNameFilter, setFirstNameFilter] = useState('');
	const [firstNameError, setFirstNameError] = useState('');

	const [error, setError] = useState('');
	const [serviceToken, setServiceToken] = useState('');
	const [propertyCode, setPropertyCode] = useState('');
	const propertyText = (uiKey: string, fallback: string) => {
		const property = uiProperties?.find(
			(item: any) => String(item?.uiKey || '').toLowerCase() === uiKey
		);
		const value = property?.uiValue;

		return value !== null && value !== undefined && String(value).trim()
			? String(value)
			: t(fallback);
	};

	/**
	 * ---------------------------------------------------------
	 * Property
	 * ---------------------------------------------------------
	 */
	useEffect(() => {
		const pcode =
			searchParams.get('PropertyCode') ||
			searchParams.get('propertyCode') ||
			searchParams.get('propertycode') ||
			searchParams.get('hotel') ||
			'';

		const finalCode = pcode || '';

		setPropertyCode(finalCode);

		if (finalCode && !propertyInfo) {
			dispatch(fetchPropertyInfoByCode(finalCode));
		}
	}, [searchParams, propertyInfo, dispatch]);

	/**
	 * ---------------------------------------------------------
	 * Property branding (UI properties)
	 * Runs on page load: service token -> GetUIPropertiesByProperty,
	 * branding is applied to :root before the page is shown.
	 * ---------------------------------------------------------
	 */
	useEffect(() => {
		const svcKey = propertyInfo?.licenseCode || '';
		const code = propertyCode || propertyInfo?.propertyCode || '';

		if (!svcKey || !code || uiProperties) return;

		let cancelled = false;

		generateServiceToken(svcKey, code)
			.then((resp) => {
				const token =
					resp?.data?.data?.token ?? resp?.data?.token ?? resp?.data;

				if (!cancelled && token) {
					dispatch(fetchUIProperties(token));
				}
			})
			.catch((err) =>
				console.error('Generate service token for branding failed:', err)
			);

		return () => {
			cancelled = true;
		};
	}, [propertyInfo, propertyCode, uiProperties, dispatch]);

	/**
	 * Property logo
	 */
	const logoUrl =
		propertyInfo?.logo ||
		propertyInfo?.logoUrl ||
		propertyInfo?.propertyLogo ||
		process.env.PUBLIC_URL + '/assets/icons/logo-hd.png';
	const tagImageUrl = uiProperties?.find(
		(item: any) => String(item?.uiKey || '').toLowerCase() === 'tagimage'
	)?.uiValue;

	/**
	 * ---------------------------------------------------------
	 * First-name filter
	 * ---------------------------------------------------------
	 */
	const applyFirstNameFilter = () => {
		setFirstNameError('');

		const name = firstNameFilter.trim().toLowerCase();

		if (!name) {
			setFirstNameError(t('Please enter first name'));
			return;
		}

		if (!results) return;

		const matches = results.filter((r: any) => {
			const firstName = String(r.FirstName || '')
				.trim()
				.toLowerCase();

			const fullName = String(r.FullName || '')
				.trim()
				.toLowerCase();

			return firstName.includes(name) || fullName.includes(name);
		});

		if (matches.length === 0) {
			setFirstNameError(t('No reservation found with that first name'));
			return;
		}

		if (matches.length > 1) {
			setFirstNameError(
				t(
					'Multiple reservations found. Please proceed to the front desk for assistance.'
				)
			);
			return;
		}

		setDisplayedResults([matches[0]]);
		setShowFirstNamePrompt(false);
		setFirstNameFilter('');
		setFirstNameError('');

		void handleProceedToPreCheckin(matches[0]);
	};

	/**
	 * ---------------------------------------------------------
	 * Search by last name + departure date
	 * ---------------------------------------------------------
	 */
	const handleSearch = async (e: React.FormEvent) => {
		e.preventDefault();

		setError('');
		setResults(null);
		setDisplayedResults(null);

		if (!lastName.trim()) {
			setError(t('Please enter last name'));
			return;
		}

		if (!departureDate) {
			setError(t('Please select departure date'));
			return;
		}

		const svcKey = propertyInfo?.licenseCode || '';
		const code = propertyCode || propertyInfo?.propertyCode || '';

		if (!svcKey) {
			setError(t('Service key is missing'));
			return;
		}

		setLoading(true);

		try {
			const tokenResponse = await generateServiceToken(
				svcKey,
				propertyInfo?.propertyCode || ''
			);

			const token =
				tokenResponse?.data?.data?.token ??
				tokenResponse?.data?.token ??
				tokenResponse?.data;

			if (!token) {
				setError(t('Unable to generate service token'));
				return;
			}

			setServiceToken(token);

			const response = await searchReservationByLastNameAndDeparture(
				lastName.trim(),
				departureDate,
				code,
				token
			);

			const data = response?.data?.data ?? response?.data ?? null;

			if (!data || (Array.isArray(data) && data.length === 0)) {
				setError(t('No reservations found'));
				return;
			}

			if (Array.isArray(data)) {
				setResults(data);

				if (data.length > 1) {
					setFirstNameFilter('');
					setFirstNameError('');
					setShowFirstNamePrompt(true);
				} else {
					setDisplayedResults(data);

					// Automatically continue when only one reservation exists
					void handleProceedToPreCheckin(data[0], token);
				}
			} else {
				setResults([data]);
				setDisplayedResults([data]);

				void handleProceedToPreCheckin(data, token);
			}
		} catch (err: any) {
			console.error('Reservation search failed:', err);

			setError(
				t(err?.response?.data?.message ?? err?.message ?? 'Search failed')
			);
		} finally {
			setLoading(false);
		}
	};

	/**
	 * ---------------------------------------------------------
	 * Search by confirmation number
	 * ---------------------------------------------------------
	 *
	 * IMPORTANT:
	 * Replace `searchReservationByConfirmationNumber` below with
	 * the confirmation-number API function from your project.
	 */
	const handleConfirmationSearch = async (e: React.FormEvent) => {
		e.preventDefault();

		setError('');
		setResults(null);
		setDisplayedResults(null);

		if (!confirmationNumber.trim()) {
			setError(t('Please enter confirmation number'));
			return;
		}

		const svcKey = propertyInfo?.licenseCode || '';
		const code = propertyCode || propertyInfo?.propertyCode || '';

		if (!svcKey) {
			setError(t('Service key is missing'));
			return;
		}

		setLoading(true);

		try {
			const tokenResponse = await generateServiceToken(
				svcKey,
				propertyInfo?.propertyCode || ''
			);

			const token =
				tokenResponse?.data?.data?.token ??
				tokenResponse?.data?.token ??
				tokenResponse?.data;

			if (!token) {
				setError(t('Unable to generate service token'));
				return;
			}

			setServiceToken(token);

			/*
			 * Replace this call with your actual API.
			 *
			 * Example:
			 *
			 * const response =
			 *   await searchReservationByConfirmationNumber(
			 *     confirmationNumber.trim(),
			 *     code,
			 *     token
			 *   );
			 */

			const response = await getReservationByConfirmationNumber(
				confirmationNumber.trim(),
				code,
				token
			);

			const data = response?.data?.data ?? response?.data ?? null;

			if (!data || (Array.isArray(data) && data.length === 0)) {
				setError(t('No reservation found'));
				return;
			}
			const isPrecheckInAllowed = response?.data?.isPrecheckInAllowed ?? false;
			if (!isPrecheckInAllowed) {
				setError(t('Pre-check-in is not allowed for this reservation'));
				return;
			}

			const reservation = Array.isArray(data) ? data[0] : data;

			setResults(Array.isArray(data) ? data : [data]);
			setDisplayedResults([reservation]);

			void handleProceedToPreCheckin(reservation, token);
		} catch (err: any) {
			console.error('Confirmation search failed:', err);

			setError(
				t(err?.response?.data?.message ?? err?.message ?? 'Search failed')
			);
		} finally {
			setLoading(false);
		}
	};

	/**
	 * ---------------------------------------------------------
	 * Pre-check-in
	 * ---------------------------------------------------------
	 */
	async function handleProceedToPreCheckin(
		reservation: any,
		tokenOverride?: string
	) {
		setError('');

		const activeServiceToken = tokenOverride || serviceToken;

		const confirmationNo =
			reservation?.pMSConfirmationNo ||
			reservation?.pmsConfirmationNo ||
			reservation?.PMSConfirmationNo ||
			reservation?.confirmationNo ||
			reservation?.ConfirmationNo ||
			reservation?.reservationID ||
			reservation?.ReservationID ||
			'';

		if (!confirmationNo) {
			setError(t('Confirmation number is missing'));
			return;
		}

		if (!activeServiceToken) {
			setError(t('Service token is missing'));
			return;
		}

		const resolvedPropertyCode =
			propertyCode || propertyInfo?.propertyCode || '';

		try {
			try {
				await updateStageStatus(
					{
						PMSConfirmationNo: String(confirmationNo),
						StepCode: 'SEI01',
						IsSuccess: true,
						SuccessMessage: 'QR code reservation search',
						FailureReason: '',
						AppType: 'engage',
						PropertyCode: resolvedPropertyCode,
						SubStep: 'QR code',
						CreatedBy: 'QR Reservation',
						KioskID: null,
					},
					activeServiceToken
				);
			} catch (err: any) {
				console.error('Inner try block failed:', err);
			}

			const resp = await generatePreCheckinLinkToken(
				confirmationNo,
				resolvedPropertyCode,
				activeServiceToken
			);

			const token =
				resp?.data?.data?.token ?? resp?.data?.token ?? resp?.data ?? null;

			if (!token) {
				setError(t('Unable to generate pre-check-in token'));
				return;
			}

			const appUrl =
				propertyInfo?.precheckinAppUrl ||
				propertyInfo?.precheckinUrl ||
				propertyInfo?.appUrl ||
				window.location.origin;

			const base = appUrl.endsWith('/') ? appUrl.slice(0, -1) : appUrl;

			bypassConfirmLeaveOnce();

			window.location.href = `${base}/?token=${encodeURIComponent(token)}`;
		} catch (err: any) {
			console.error('Generate precheckin token failed', err);

			setError(
				t(err?.response?.data?.message ?? err?.message ?? 'Operation failed')
			);
		}
	}

	if (propertyCode && (!propertyInfo || !uiProperties)) {
		return (
			<div className="theme-cover qr-page qr-loading-page">
				<div className="qr-loading-content">
					<div className="spinner-grow mb-3" role="status" />
					<p>
						{propertyText(
							'qr-loading-message',
							'Please wait while we load the details'
						)}
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="theme-cover qr-page">
			{/* =====================================================
				    HEADER — property logo + title
				===================================================== */}
			<div className="qr-header">
				<img
					src={logoUrl}
					alt={propertyInfo?.propertyName || t('Property logo')}
					className="qr-logo"
				/>

				{tagImageUrl && String(tagImageUrl).trim() && (
					<img
						src={String(tagImageUrl).trim()}
						style={{ padding: '10px', margin: 'auto' }}
						alt=""
					/>
				)}
				<h1 className="qr-hero-title primary-double-border">
					{propertyText('tagline', t('Search your reservation'))}
				</h1>
			</div>

			{/* =====================================================
				    WHITE CONTENT SHEET
				===================================================== */}
			<div className="qr-sheet">
				<div className="qr-sheet-inner">
					{/* =================================================
						    LAST NAME + DEPARTURE DATE
						================================================= */}
					{searchMode === 'details' && (
						<>
							<h2 className="qr-section-title">
								{propertyText('qr-section-title', 'Enter your details')}
							</h2>

							<p className="qr-section-description">
								{propertyText(
									'qr-details-description',
									t('Use the last name on the booking and your departure date.')
								)}
							</p>

							<form onSubmit={handleSearch}>
								{/* Last name */}
								<div className="mb-4">
									<label className="qr-label">
										{propertyText('qr-last-name-label', 'Last name')}
									</label>

									<input
										type="text"
										className="qr-field"
										value={lastName}
										placeholder={propertyText(
											'qr-last-name-placeholder',
											'Last name'
										)}
										autoComplete="family-name"
										onChange={(e) => setLastName(e.target.value)}
									/>
								</div>

								{/* Departure date */}
								<div className="mb-4">
									<label className="qr-label">
										{propertyText('qr-departure-date-label', 'Departure date')}
									</label>

									<div className="qr-date-wrapper">
										<DatePicker
											selected={departureDateObj}
											onChange={(date: Date | null) => {
												setDepartureDateObj(date);

												if (date) {
													const y = date.getFullYear();
													const m = String(date.getMonth() + 1).padStart(
														2,
														'0'
													);
													const d = String(date.getDate()).padStart(2, '0');

													setDepartureDate(`${y}-${m}-${d}`);
												} else {
													setDepartureDate('');
												}
											}}
											placeholderText={propertyText(
												'qr-select-date-placeholder',
												'Select date'
											)}
											className={`qr-date-picker ${styles.reactwrapper}`}
											wrapperClassName={`qr-date-picker-wrap ${styles.datePicker}`}
											dateFormat="dd MMM yyyy"
											minDate={new Date()}
											showPreviousMonths={false}
											showMonthDropdown
											showYearDropdown
											scrollableYearDropdown
											yearDropdownItemNumber={100}
											withPortal
										/>
										<FontAwesomeIcon
											icon={faCalendar}
											className="qr-calendar"
										/>
									</div>
								</div>

								{/* Error */}
								{error && <div className="qr-error">{error}</div>}

								{/* Search */}
								<button
									type="submit"
									className="qr-primary-button"
									disabled={loading}>
									{loading ? (
										<>
											<span
												className="spinner-border spinner-border-sm me-2"
												aria-hidden="true"
											/>
											{propertyText('qr-searching-label', 'Searching...')}
										</>
									) : (
										<>
											{propertyText('qr-search-button', 'Search reservation')}
											<FontAwesomeIcon icon={faChevronRight} />
										</>
									)}
								</button>
							</form>

							{/* OR */}
							<div className="qr-or">
								<span>{propertyText('qr-or-label', 'OR')}</span>
							</div>

							{/* Confirmation number */}
							<button
								type="button"
								className="qr-secondary-button"
								onClick={() => {
									setError('');
									setSearchMode('confirmation');
								}}>
								{propertyText(
									'qr-search-by-confirmation-button',
									'Search with confirmation number'
								)}
							</button>
						</>
					)}

					{/* =================================================
						    CONFIRMATION NUMBER
						================================================= */}
					{searchMode === 'confirmation' && (
						<>
							<button
								type="button"
								className="qr-back"
								onClick={() => {
									setError('');
									setSearchMode('details');
								}}>
								{propertyText('qr-back-button', '← Back')}
							</button>

							<h2 className="qr-section-title">
								{propertyText('qr-section-title', 'Enter your details')}
							</h2>

							<p className="qr-section-description">
								{propertyText(
									'qr-confirmation-description',
									'Enter your booking confirmation number'
								)}
							</p>

							<form onSubmit={handleConfirmationSearch}>
								<div className="mb-4">
									<label className="qr-label">
										{propertyText(
											'qr-confirmation-number-label',
											'Confirmation number'
										)}
									</label>

									<input
										autoFocus
										type="text"
										className="qr-field"
										value={confirmationNumber}
										placeholder={propertyText(
											'qr-confirmation-number-placeholder',
											'Confirmation number'
										)}
										onChange={(e) => setConfirmationNumber(e.target.value)}
									/>
								</div>

								{error && <div className="qr-error">{error}</div>}

								<button
									type="submit"
									className="qr-primary-button"
									disabled={loading}>
									{loading ? (
										<>
											<span
												className="spinner-border spinner-border-sm me-2"
												aria-hidden="true"
											/>
											{propertyText('qr-searching-label', 'Searching...')}
										</>
									) : (
										<>
											{propertyText('qr-search-button', 'Search reservation')}
											<FontAwesomeIcon icon={faChevronRight} />
										</>
									)}
								</button>
							</form>

							<div className="qr-or">
								<span>{propertyText('qr-or-label', 'OR')}</span>
							</div>

							<button
								type="button"
								className="qr-secondary-button"
								onClick={() => {
									setError('');
									setSearchMode('details');
								}}>
								{propertyText(
									'qr-search-by-last-name-button',
									'Search with Lastname'
								)}
							</button>
						</>
					)}

					{/* =================================================
						    SECURITY FOOTER
						================================================= */}
					<div className="qr-footer">
						<FontAwesomeIcon icon={faLock} className="me-2" />
						{propertyText(
							'qr-security-message',
							'Your reservation information is securely handled'
						)}
					</div>
				</div>
			</div>

			{/* =====================================================
				    FIRST NAME MODAL
				===================================================== */}
			{showFirstNamePrompt && results && (
				<div className="qr-first-name-modal">
					<div className="qr-first-name-card">
						<h3 className="qr-section-title mb-2">
							{propertyText('qr-first-name-title', 'Confirm First Name')}
						</h3>

						<p className="text-muted mb-4">
							{propertyText(
								'qr-first-name-description',
								`Multiple reservations were found. Please enter the guest's first name to show the correct reservation.`
							)}
						</p>

						<input
							autoFocus
							type="text"
							className="qr-field mb-2"
							value={firstNameFilter}
							onChange={(e) => setFirstNameFilter(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === 'Enter') {
									applyFirstNameFilter();
								}
							}}
							placeholder={propertyText(
								'qr-first-name-placeholder',
								'Enter first name'
							)}
						/>

						{firstNameError && (
							<div className="text-danger small mb-3">{firstNameError}</div>
						)}

						<div className="qr-modal-actions">
							<button
								type="button"
								className="qr-secondary-button"
								onClick={() => {
									setShowFirstNamePrompt(false);
									setFirstNameFilter('');
									setFirstNameError('');
								}}>
								{propertyText('qr-cancel-button', 'Cancel')}
							</button>

							<button
								type="button"
								className="qr-primary-button"
								onClick={applyFirstNameFilter}>
								{propertyText('qr-continue-button', 'Continue')}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
